/**
 * IncidentDetailPage — investigation view. Shows the full incident
 * record, corrective actions list (add / mark complete / delete),
 * and a Resolve button for PM/admin. Reporter can edit while status
 * is "open"; the resolve action locks the record for admins to
 * eventually close.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import IncidentPhotosSection from "@/components/IncidentPhotosSection";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ShieldAlert, HeartPulse, AlertTriangle, Wrench, CheckCircle2, Circle, Trash2, Plus, User, Clock, MapPin, FileText, Download } from "lucide-react";

const TYPE_META = {
  stop_work:       { label: "Stop-Work",       icon: ShieldAlert,   ring: "border-red-800 bg-red-950/30" },
  injury:          { label: "Injury",          icon: HeartPulse,    ring: "border-red-800 bg-red-950/30" },
  near_miss:       { label: "Near Miss/Good Catch", icon: AlertTriangle, ring: "border-amber-800 bg-amber-950/30" },
  property_damage: { label: "Property Damage", icon: Wrench,        ring: "border-amber-800 bg-amber-950/30" },
};
const STATUS_CLS = {
  open:          "bg-red-900/60 text-red-200 border-red-800",
  investigating: "bg-amber-900/60 text-amber-200 border-amber-800",
  resolved:      "bg-emerald-900/60 text-emerald-200 border-emerald-800",
  closed:        "bg-slate-800 text-slate-300 border-slate-700",
};

export default function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incident, setIncident] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveSummary, setResolveSummary] = useState("");
  const [rootCauseDraft, setRootCauseDraft] = useState("");
  const [newCA, setNewCA] = useState({ action: "", assigned_to: "", due_date: "" });

  const reload = () => {
    api.get(`/incidents/${id}`)
      .then((r) => { setIncident(r.data); setRootCauseDraft(r.data.root_cause || ""); })
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); });
  };
  useEffect(reload, [id]);

  const canEdit = useMemo(() => {
    if (!user || !incident) return false;
    if (user.role === "admin" || user.role === "pm") return true;
    if (incident.reported_by === user.id && incident.status === "open") return true;
    return false;
  }, [user, incident]);
  const canResolve = user?.role === "admin" || user?.role === "pm";

  if (notFound) return <AppShell><div className="text-center py-20 text-slate-400" data-testid="incident-not-found">Incident not found.</div></AppShell>;
  if (!incident) return <AppShell><div className="text-center py-20 text-slate-500" data-testid="incident-loading">Loading…</div></AppShell>;

  const meta = TYPE_META[incident.type] || TYPE_META.near_miss;
  const Icon = meta.icon;

  const saveRootCause = async () => {
    try {
      await api.patch(`/incidents/${id}`, { root_cause: rootCauseDraft });
      toast.success("Saved.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const startInvestigation = async () => {
    try {
      await api.patch(`/incidents/${id}`, { status: "investigating" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };
  const reopen = async () => {
    try {
      await api.patch(`/incidents/${id}`, { status: "investigating" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed."); }
  };

  const doResolve = async () => {
    if (!resolveSummary.trim()) return toast.error("Summarize the resolution first.");
    try {
      await api.post(`/incidents/${id}/resolve`, { resolution_summary: resolveSummary });
      toast.success("Incident resolved.");
      setResolveOpen(false); setResolveSummary("");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resolve failed."); }
  };
  const doClose = async () => {
    try {
      await api.post(`/incidents/${id}/close`);
      toast.success("Incident closed.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed."); }
  };
  const doDelete = async () => {
    if (!window.confirm("Delete this incident permanently? This cannot be undone.")) return;
    try {
      await api.delete(`/incidents/${id}`);
      toast.success("Deleted.");
      navigate("/incidents");
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const addCA = async () => {
    if (!newCA.action.trim()) return toast.error("Describe the action.");
    try {
      await api.post(`/incidents/${id}/corrective-actions`, newCA);
      setNewCA({ action: "", assigned_to: "", due_date: "" });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add action."); }
  };
  const toggleCA = async (ca) => {
    try {
      await api.patch(`/incidents/${id}/corrective-actions/${ca.id}`, { completed: !ca.completed_at });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const deleteCA = async (ca) => {
    try {
      await api.delete(`/incidents/${id}/corrective-actions/${ca.id}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const done = (incident.corrective_actions || []).filter(c => c.completed_at).length;
  const total = (incident.corrective_actions || []).length;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto space-y-4">
        <Link to="/incidents" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-200" data-testid="incident-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to Incidents
        </Link>

        {/* Header card */}
        <Card className={`border ${meta.ring}`} data-testid="incident-header-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="p-2.5 rounded-md border border-slate-700 bg-slate-950 text-slate-100 shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-slate-400 font-display">Safety Incident</div>
                  <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight" data-testid="incident-title">{meta.label}</h1>
                  <div className="text-xs text-slate-400 mt-1 flex items-center flex-wrap gap-x-3 gap-y-1">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {incident.site_number || "—"} · {incident.customer || "—"}</span>
                    <span className="inline-flex items-center gap-1"><User className="w-3 h-3" /> {incident.reported_by_name}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {incident.occurred_at?.slice(0, 16).replace("T", " ")}</span>
                  </div>
                  {incident.project_id && (
                    <Link to={`/projects/${incident.project_id}`} className="text-xs text-blue-400 hover:text-blue-200 underline underline-offset-2 mt-1 inline-block" data-testid="incident-project-link">
                      View linked project →
                    </Link>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${STATUS_CLS[incident.status]}`} data-testid="incident-status-badge">
                  {incident.status}
                </span>
                <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-slate-800 text-slate-200">
                  {incident.severity}
                </span>
              </div>
            </div>

            {/* Actions strip */}
            <div className="flex flex-wrap gap-2 mt-4">
              {incident.status === "open" && canEdit && (
                <Button size="sm" onClick={startInvestigation} className="bg-amber-700 hover:bg-amber-600" data-testid="incident-start-invest">
                  Start Investigation
                </Button>
              )}
              {incident.status !== "resolved" && incident.status !== "closed" && canResolve && (
                <Button size="sm" onClick={() => setResolveOpen(!resolveOpen)} className="bg-emerald-700 hover:bg-emerald-600" data-testid="incident-resolve-btn">
                  {resolveOpen ? "Cancel resolve" : "Resolve"}
                </Button>
              )}
              {incident.status === "resolved" && canResolve && (
                <>
                  <Button size="sm" onClick={doClose} className="bg-slate-700 hover:bg-slate-600" data-testid="incident-close-btn">Close</Button>
                  <Button size="sm" variant="outline" onClick={reopen} className="border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="incident-reopen-btn">Re-open</Button>
                </>
              )}
              <Button
                size="sm"
                variant="outline"
                onClick={() => { window.location.href = `${API_BASE}/incidents/${incident.id}/investigation.pdf`; }}
                className="border-blue-800 bg-blue-950/40 hover:bg-blue-950 text-blue-200"
                data-testid="incident-pdf-btn"
              >
                <Download className="w-3.5 h-3.5 mr-1" /> Investigation PDF
              </Button>
              {user?.role === "admin" && (
                <Button size="sm" variant="outline" onClick={doDelete} className="border-red-800 bg-red-950/40 hover:bg-red-950 text-red-300" data-testid="incident-delete-btn">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                </Button>
              )}
            </div>

            {resolveOpen && (
              <div className="mt-4 p-3 rounded-md border border-emerald-900 bg-emerald-950/20" data-testid="incident-resolve-panel">
                <Label className="text-[10px] uppercase tracking-wider text-emerald-300 font-display font-bold block mb-1">
                  Resolution summary
                </Label>
                <Textarea rows={3} value={resolveSummary} onChange={(e) => setResolveSummary(e.target.value)}
                  placeholder="Summarize what was done, evidence collected, sign-offs obtained. This lands on the incident report."
                  className="bg-slate-950 border-slate-700 mb-2" data-testid="incident-resolve-summary" />
                <div className="text-right">
                  <Button size="sm" onClick={doResolve} className="bg-emerald-700 hover:bg-emerald-600" data-testid="incident-resolve-confirm">Confirm resolve</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Description + immediate action */}
        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-5 space-y-4">
            <Section title="What Happened" testid="incident-what-happened">
              <p className="text-sm text-slate-200 whitespace-pre-wrap">{incident.description}</p>
              {incident.immediate_action_taken && (
                <div className="mt-3">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-display font-bold mb-1">Immediate action taken</div>
                  <p className="text-sm text-slate-300 whitespace-pre-wrap">{incident.immediate_action_taken}</p>
                </div>
              )}
              {incident.occurred_location && (
                <div className="mt-3 text-xs text-slate-400">Location on site: <span className="text-slate-200">{incident.occurred_location}</span></div>
              )}
            </Section>

            {incident.type === "injury" && (
              <Section title="Injury Details" testid="incident-injury-details">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <KV k="Injured person" v={incident.injured_person_name || "—"} />
                  <KV k="Body part" v={incident.body_part || "—"} />
                  <KV k="Treatment" v={incident.treatment_level || "—"} />
                  <KV k="Days away" v={incident.days_away} />
                  <KV k="Days restricted" v={incident.days_restricted} />
                  <KV k="OSHA recordable" v={incident.osha_recordable ? "Yes" : "No"} />
                  <KV k="OSHA reportable" v={incident.osha_reportable ? "Yes" : "No"} />
                </div>
              </Section>
            )}

            {/* Emergency services block — visible whenever we've
                captured any emergency data (regardless of current
                severity, so escalations that later get downgraded
                still surface the record). */}
            {(incident.emergency_services_contacted
              || incident.emergency_services_dispatched
              || (incident.emergency_agencies || "").trim()
              || (incident.emergency_case_number || "").trim()
              || (incident.emergency_notes || "").trim()
              || incident.severity === "high"
              || incident.severity === "critical") && (
              <Section title="Emergency Services" testid="incident-emergency-details">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  <KV k="Contacted"   v={incident.emergency_services_contacted ? "Yes" : "No"} />
                  <KV k="Dispatched"  v={incident.emergency_services_dispatched ? "Yes" : (incident.emergency_services_contacted ? "No" : "—")} />
                  <KV k="Agencies"    v={incident.emergency_agencies || "—"} />
                  <KV k="Arrival"     v={incident.emergency_arrival_time?.replace("T", " ")?.slice(0, 16) || "—"} />
                  <KV k="Case #"      v={incident.emergency_case_number || "—"} />
                </div>
                {incident.emergency_notes && (
                  <p className="text-xs text-slate-300 mt-2 whitespace-pre-wrap">{incident.emergency_notes}</p>
                )}
              </Section>
            )}

            <Section title="Root Cause" testid="incident-root-cause">
              {canEdit ? (
                <div className="space-y-2">
                  <Textarea rows={3} value={rootCauseDraft} onChange={(e) => setRootCauseDraft(e.target.value)}
                    placeholder="What ultimately caused this? What system/process broke down?"
                    className="bg-slate-950 border-slate-700" data-testid="incident-root-cause-input" />
                  <div className="text-right">
                    <Button size="sm" onClick={saveRootCause} className="bg-blue-700 hover:bg-blue-600" data-testid="incident-root-cause-save">Save</Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-300 whitespace-pre-wrap">{incident.root_cause || <span className="italic text-slate-500">Not yet documented.</span>}</p>
              )}
            </Section>

            <Section title={`Corrective Actions (${done}/${total})`} testid="incident-cas">
              {(incident.corrective_actions || []).length === 0 ? (
                <div className="text-sm text-slate-500 italic">No corrective actions logged yet.</div>
              ) : (
                <div className="space-y-2">
                  {incident.corrective_actions.map((ca) => {
                    const canToggle = canEdit || (ca.assigned_to_user_id === user?.id);
                    return (
                      <div key={ca.id} className={`p-3 rounded-md border ${ca.completed_at ? "border-emerald-900 bg-emerald-950/20" : "border-slate-800 bg-slate-950"}`} data-testid={`incident-ca-${ca.id}`}>
                        <div className="flex items-start gap-3">
                          <button
                            type="button"
                            disabled={!canToggle}
                            onClick={() => toggleCA(ca)}
                            className="mt-0.5 shrink-0 disabled:opacity-40"
                            data-testid={`incident-ca-toggle-${ca.id}`}
                          >
                            {ca.completed_at
                              ? <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                              : <Circle className="w-5 h-5 text-slate-500" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className={`text-sm ${ca.completed_at ? "text-slate-400 line-through" : "text-slate-100"}`}>{ca.action}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 font-mono">
                              {ca.assigned_to && `${ca.assigned_to}`}
                              {ca.due_date && ` · due ${ca.due_date}`}
                              {ca.completed_at && ` · done ${ca.completed_at.slice(0, 10)} by ${ca.completed_by_name || "?"}`}
                            </div>
                          </div>
                          {canEdit && (
                            <button type="button" onClick={() => deleteCA(ca)} className="text-slate-500 hover:text-red-400 shrink-0" data-testid={`incident-ca-delete-${ca.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {canEdit && (
                <div className="mt-3 p-3 rounded-md border border-slate-800 bg-slate-950">
                  <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                    <Input placeholder="What needs to happen?" value={newCA.action} onChange={(e) => setNewCA({ ...newCA, action: e.target.value })} className="sm:col-span-3 bg-slate-900 border-slate-700" data-testid="incident-ca-new-action" />
                    <Input placeholder="Assigned to" value={newCA.assigned_to} onChange={(e) => setNewCA({ ...newCA, assigned_to: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="incident-ca-new-assignee" />
                    <Input type="date" value={newCA.due_date} onChange={(e) => setNewCA({ ...newCA, due_date: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="incident-ca-new-due" />
                    <Button onClick={addCA} className="bg-blue-700 hover:bg-blue-600" data-testid="incident-ca-add-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  </div>
                </div>
              )}
            </Section>

            {incident.status === "resolved" || incident.status === "closed" ? (
              <Section title="Resolution" testid="incident-resolution">
                <p className="text-sm text-slate-200 whitespace-pre-wrap">{incident.resolution_summary}</p>
                <div className="text-[11px] text-slate-500 mt-1 font-mono">
                  {incident.resolved_by_name && `Resolved by ${incident.resolved_by_name}`}
                  {incident.resolved_at && ` · ${incident.resolved_at.slice(0, 16).replace("T", " ")}`}
                </div>
              </Section>
            ) : null}

            {incident.auto_created && (
              <div className="p-2.5 rounded-md border border-slate-800 bg-slate-950 text-xs text-slate-400 flex items-start gap-2" data-testid="incident-auto-created-note">
                <FileText className="w-3.5 h-3.5 mt-0.5" />
                Created automatically from a Site Audit that issued a stop-work. Investigation fields are pre-populated where possible.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Evidence photos — separate card so it can be added/removed
            independently of the main narrative and shows up between
            the investigation body and the resolution history. */}
        <IncidentPhotosSection incidentId={id} canEdit={canEdit} onChanged={reload} />
      </div>
    </AppShell>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-display font-bold mb-2">{title}</div>
      {children}
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div className="p-2 rounded border border-slate-800 bg-slate-950">
      <div className="text-[9px] uppercase tracking-wider text-slate-500">{k}</div>
      <div className="text-slate-200 font-mono">{v ?? "—"}</div>
    </div>
  );
}
